import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, takeUntil } from 'rxjs/operators';

import { ApiService } from 'src/app/shared/services/api.service';
import {
  VisitorCO,
  VisitorStatus,
  EnquiryType,
  SourceType,
  SchoolCO,
  AddressCO
} from 'src/app/shared/models/ApiModels';
import { environment } from 'src/environments/environment';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { SchoolDetailsService } from 'src/app/shared/services/school-details.service';

interface ContactFormModel {
  visitorName: FormControl<string | null>;
  emailId: FormControl<string | null>;
  phoneNo: FormControl<string | null>;
  town: FormControl<string | null>;
  remarks: FormControl<string | null>;
}

@Component({
  selector: 'app-contact-us',
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.scss']
})
export class ContactUsComponent implements OnInit, OnDestroy {

  contactForm!: FormGroup<ContactFormModel>;
  loading = false;

  /** backend model */
  model: VisitorCO = {} as VisitorCO;

  school: SchoolCO | null = null;
  latitude?: string;
  longitude?: string;
  mapUrl?: SafeResourceUrl;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private sanitizer: DomSanitizer,
    private schoolService: SchoolDetailsService
  ) {}

  ngOnInit(): void {
    this.initializeModel();
    this.buildForm();
    this.subscribeToSchoolState();
  }

  /** ---------------- INITIAL MODEL ---------------- */
  private initializeModel(): void {
    this.model.enquryType = EnquiryType.Admission;
    this.model.status = VisitorStatus.OPEN;
    this.model.school = { id: environment.schoolId } as SchoolCO;
    this.model.source = SourceType.WEBSITE;
  }

  /** ---------------- FORM ---------------- */
  private buildForm(): void {
    this.contactForm = this.fb.group<ContactFormModel>({
      visitorName: this.fb.control('', {
        nonNullable: false,
        validators: [Validators.required, Validators.minLength(3)]
      }),
      emailId: this.fb.control('', {
        validators: [Validators.required, Validators.email]
      }),
      phoneNo: this.fb.control('', {
        validators: [Validators.required, Validators.pattern(/^[0-9]{10}$/)]
      }),
      town: this.fb.control(this.model?.address?.town ?? '', {
        validators: Validators.required
      }),
      remarks: this.fb.control('', {
        validators: [Validators.required, Validators.minLength(10)]
      })
    });
  }

  /** ---------------- SUBMIT ---------------- */
  submit(): void {
    if (this.contactForm.invalid || this.loading) {
      this.contactForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const payload = this.buildVisitorPayload();

    this.apiService.createVisitorEnquiry(payload)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (res: any) => {
          if (res?.statusCode === 200) {
            this.snackBar.open('Enquiry submitted successfully!', 'Close', { duration: 3000 });
            this.contactForm.reset({
              visitorName: '',
              emailId: '',
              phoneNo: '',
              town: '',
              remarks: ''
            });
          } else {
            this.snackBar.open(res?.message || 'Submission failed', 'Close', { duration: 3000 });
          }
        },
        error: () => {
          this.snackBar.open('Server error. Please try again later.', 'Close', { duration: 3000 });
        }
      });
  }

  /** ---------------- PAYLOAD BUILDER ---------------- */
  private buildVisitorPayload(): VisitorCO {
    const formValue = this.contactForm.getRawValue();

    const now = new Date();
    this.model.enquirydate = now.toLocaleDateString('en-GB');
    this.model.timeIN = now.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });

    return {
      ...this.model,
      visitorName: formValue.visitorName,
      emailId: formValue.emailId,
      phoneNo: formValue.phoneNo,
      remarks: formValue.remarks,
      address: {
        town: formValue.town
      } as AddressCO,
      existingParent: false
    } as VisitorCO;
  }

  /** ---------------- INPUT HELPERS ---------------- */
  allowOnlyNumbers(event: KeyboardEvent): void {
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  get f() {
    return this.contactForm.controls;
  }

  private subscribeToSchoolState(): void {
    this.schoolService.schoolState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (state.loading) {
          return;
        }

        if (state.error) {
          this.showError(state.error);
          this.resetLocation();
          return;
        }

        if (!state.data) {
          this.resetLocation();
          return;
        }

        this.school = state.data;

        const location = state.data.paymentInfo?.location;
        if (!location?.latitude || !location?.longitude) {
          this.resetLocation();
          return;
        }

        this.latitude = location.latitude;
        this.longitude = location.longitude;

        const url = `https://www.google.com/maps?q=${this.latitude},${this.longitude}&hl=en&z=15&output=embed`;
        this.mapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetLocation(): void {
    this.latitude = undefined;
    this.longitude = undefined;
    this.mapUrl = undefined;
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'x', { duration: 3000 });
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'x', { duration: 3000 });
  }
}
